import { useState, useEffect, useCallback } from 'react';
import './AccessibilityWidget.css';

const STORAGE_KEY = 'gfs-a11y-settings';

const defaultSettings = {
  fontSize: 0,
  highContrast: false,
  highlightLinks: false,
  dyslexiaFont: false,
  reducedMotion: false,
};

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });

  const applySettings = useCallback((s) => {
    const root = document.documentElement;

    // Font size: -2 to +4 steps
    const sizePercent = 100 + s.fontSize * 12.5;
    root.style.fontSize = s.fontSize === 0 ? '' : `${sizePercent}%`;

    // High contrast
    root.classList.toggle('a11y-high-contrast', s.highContrast);

    // Highlight links
    root.classList.toggle('a11y-highlight-links', s.highlightLinks);

    // Dyslexia-friendly font
    root.classList.toggle('a11y-dyslexia-font', s.dyslexiaFont);

    // Reduced motion
    root.classList.toggle('a11y-reduced-motion', s.reducedMotion);
  }, []);

  useEffect(() => {
    applySettings(settings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // ignore
    }
  }, [settings, applySettings]);

  const update = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const reset = () => {
    setSettings(defaultSettings);
  };

  const isModified = JSON.stringify(settings) !== JSON.stringify(defaultSettings);

  return (
    <>
      <button
        className="a11y-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open accessibility options"
        title="Accessibility options"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28" aria-hidden="true">
          <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm9 5H3v2h6.5v4l-2 7h2.2l1.8-6.3L13.3 20h2.2l-2-7V9H20V7Z"/>
        </svg>
      </button>

      {open && (
        <div
          className="a11y-panel"
          role="dialog"
          aria-label="Accessibility settings"
        >
          <div className="a11y-panel__header">
            <h2 className="a11y-panel__title">Accessibility</h2>
            <button
              className="a11y-panel__close"
              onClick={() => setOpen(false)}
              aria-label="Close accessibility options"
            >
              &times;
            </button>
          </div>

          <div className="a11y-panel__body">
            {/* Font size */}
            <div className="a11y-control">
              <span className="a11y-control__label">Font Size</span>
              <div className="a11y-control__actions">
                <button
                  onClick={() => update('fontSize', Math.max(-2, settings.fontSize - 1))}
                  disabled={settings.fontSize <= -2}
                  aria-label="Decrease font size"
                >
                  A−
                </button>
                <span className="a11y-control__value">
                  {settings.fontSize === 0 ? 'Default' : `${settings.fontSize > 0 ? '+' : ''}${settings.fontSize}`}
                </span>
                <button
                  onClick={() => update('fontSize', Math.min(4, settings.fontSize + 1))}
                  disabled={settings.fontSize >= 4}
                  aria-label="Increase font size"
                >
                  A+
                </button>
              </div>
            </div>

            {/* High contrast */}
            <label className="a11y-control a11y-control--toggle">
              <span className="a11y-control__label">High Contrast</span>
              <input
                type="checkbox"
                checked={settings.highContrast}
                onChange={(e) => update('highContrast', e.target.checked)}
              />
              <span className="a11y-toggle-switch" />
            </label>

            {/* Highlight links */}
            <label className="a11y-control a11y-control--toggle">
              <span className="a11y-control__label">Highlight Links</span>
              <input
                type="checkbox"
                checked={settings.highlightLinks}
                onChange={(e) => update('highlightLinks', e.target.checked)}
              />
              <span className="a11y-toggle-switch" />
            </label>

            {/* Dyslexia font */}
            <label className="a11y-control a11y-control--toggle">
              <span className="a11y-control__label">Dyslexia-Friendly Font</span>
              <input
                type="checkbox"
                checked={settings.dyslexiaFont}
                onChange={(e) => update('dyslexiaFont', e.target.checked)}
              />
              <span className="a11y-toggle-switch" />
            </label>

            {/* Reduced motion */}
            <label className="a11y-control a11y-control--toggle">
              <span className="a11y-control__label">Reduce Motion</span>
              <input
                type="checkbox"
                checked={settings.reducedMotion}
                onChange={(e) => update('reducedMotion', e.target.checked)}
              />
              <span className="a11y-toggle-switch" />
            </label>
          </div>

          {isModified && (
            <div className="a11y-panel__footer">
              <button className="a11y-reset" onClick={reset}>
                Reset All
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
